import { PermissionEnum } from '@Application/Security/Roles';
import FetchAccountRoles from '@Application/Security/Roles/Queries/FetchAccountRoles';
import fetchAccountRoles from '@Application/Security/Roles/fetchAccountRoles';
import FetchAccountUsers from '@Application/Users/Users/Queries/FetchAccountUsers';
import FetchFacilityUsers from '@Application/Users/Users/Queries/FetchFacilityUsers';
import fetchAccountUsers from '@Application/Users/Users/fetchAccountUsers';
import fetchFacilityUsers from '@Application/Users/Users/fetchFacilityUsers';
import EmptyState from '@Guidelines/Component/EmptyState';
import ErrorState from '@Guidelines/Component/ErrorState';
import { theme } from '@Guidelines/Theme/theme';
import UserCard from '@UI/Pages/Administration/Users/UserCard';
import userFiltersSignal, {
	setUserFilters,
} from '@UI/Pages/Administration/Users/Filters/Signals/userFiltersSignal';
import UserFilterView from '@UI/Pages/Administration/Users/Filters/UserFilterView';
import { openUserSidebar } from '@UI/Pages/Administration/Users/Signals/userSidebarSignal';
import useStrictAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useStrictAuthenticatedUser';
import useCurrentFacility from '@UI/Providers/CurrentFacilityProvider/useCurrentFacility';
import useCurrentOrganization from '@UI/Providers/CurrentOrganizationProvider/useCurrentOrganization';
import useHasPermission from '@UI/Providers/PermissionProvider/useHasPermission';
import type AccountFacilityView from '@UI/Store/AccountFacilities/Views/AccountFacilityView';
import type RoleView from '@UI/Store/Roles/Views/RoleView';
import { css } from '@emotion/react';
import { Building2, TriangleAlert, UserPlus, Users as UsersIcon } from 'lucide-react';
import * as React from 'react';
import { useSignalValue } from '@verclam/pulsar';
import { CACHE_DISABLED, useDispatch, useStoreQuery } from '@verclam/synapse';
import { Button, Message, Paginator, type PaginatorPageChangeEvent, Skeleton } from '@verclam/ui-kit';

/**
 * Two equal columns; one below `layout.compactBreakpoint`. Dimmed and inert while a background refetch
 * (page/filter/sort change) is in flight — the old list stays visible but signals it is stale.
 */
const cardsGridStyle = (isFetching: boolean) => css`
	display: grid;
	grid-template-columns: 1fr 1fr;
	grid-auto-rows: auto;
	/* Each card is a subgrid spanning this grid's row tracks (see UserCard), so this row gap also
	 * becomes the card's own internal spacing between its header, pills, metadata and actions —
	 * matching the 0.875rem the card used to apply on its own. */
	gap: 0.875rem;
	margin: 0;
	padding: 0;
	list-style: none;
	opacity: ${isFetching ? 0.6 : 1};
	pointer-events: ${isFetching ? 'none' : 'auto'};

	@media (max-width: ${theme.layout.compactBreakpoint}) {
		grid-template-columns: 1fr;
	}
`;

const SKELETON_CARD_COUNT = 6;
const skeletonCardIndexes = [...Array(SKELETON_CARD_COUNT).keys()];

const containerStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1.5rem;
	overflow-y: auto;
	padding: 0 1rem;
`;

/**
 * Server-paginated, server-filtered list of users, rendered as a two-column card grid. The screen
 * picks its own route by permission (`guides/lists-and-filters.md`, `core.md` §7): a `USER_READ_ACCOUNT`
 * holder reads the whole account (`accounts/:account/users`); everyone else reads a single facility
 * (`facilities/:facility/users`), the current one from `useCurrentFacility()`. The `usersFetched` action
 * always carries the authenticated user's `accountId` as the store's ownership key, regardless of which
 * route answered — only the URL scope changes.
 *
 * `roles` feeds `UserCard`'s role pill lookup, read from `rolesStore.accountRoles` — the account's
 * whole role catalogue, distinct from the filtered `rolesList` the Roles screen owns. Agency/organization
 * data instead comes from `useCurrentOrganization()` — every organization the caller is assigned to,
 * each carrying its facilities. It is already fetched and composed once for the whole session
 * (`CurrentOrganizationProvider`), so this screen reads it rather than issuing its own queries against
 * the same two underlying resources.
 *
 * Owns only the fetch and the loading/error/empty/nominal branches of the list itself. `Filters`,
 * `UserSidebar`, `UsersConfirmationDialogs` and `UsersNotifications` are unconditional siblings of this component,
 * mounted once by the page (`index.tsx`) rather than repeated across every branch here.
 */
const UsersContent: React.FC = () => {
	const dispatch = useDispatch();
	const { user: currentUser } = useStrictAuthenticatedUser();
	const accountId = currentUser.account?.id ?? null;
	const canWrite = useHasPermission(PermissionEnum.USER_WRITE);
	const canReadAccountUsers = useHasPermission(PermissionEnum.USER_READ_ACCOUNT);
	const { currentFacility, isResolved: isFacilityResolved } = useCurrentFacility();
	const facilityId = currentFacility?.id ?? null;

	const filters = useSignalValue(userFiltersSignal);

	const {
		data: usersList,
		isLoading,
		isFetching,
		error,
		refetch: refetchUsers,
	} = useStoreQuery({
		selector: (state) => state.usersStore.users,
		stopOn: (value) => value !== null && value.accountId === accountId,
		query: () => {
			if (accountId === null) {
				return Promise.resolve();
			}

			if (canReadAccountUsers) {
				return dispatch(fetchAccountUsers(new FetchAccountUsers(accountId, filters.queryParams)));
			}

			// No facility selected on the facility route is not an error: it means the caller has no
			// assignment yet, and the screen must not call `.../facilities/undefined/users`.
			if (facilityId === null) {
				return Promise.resolve();
			}

			return dispatch(
				fetchFacilityUsers(new FetchFacilityUsers(accountId, facilityId, filters.queryParams)),
			);
		},
		// `facilityId` and `canReadAccountUsers` decide which route is called: without them here,
		// switching facility via the `FacilitySelector`, or a permission change, would not re-fetch and
		// the screen would keep showing the previous facility's users under the new title.
		deps: [filters.queryParams.toString(), accountId, canReadAccountUsers, facilityId],
		cache: CACHE_DISABLED,
	});

	// Same reasoning as `accountRoles` below: the account's whole role catalogue does not change
	// within a session, so "already populated" is a sufficient stop condition — no `cache`.
	const { data: accountRoles } = useStoreQuery({
		selector: (state) => state.rolesStore.accountRoles,
		stopOn: (value) => accountId === null || (value !== null && value.accountId === accountId),
		query: () => {
			if (accountId === null) {
				return Promise.resolve();
			}

			return dispatch(fetchAccountRoles(new FetchAccountRoles(accountId)));
		},
		deps: [accountId],
	});

	const { organizations, isResolved: areOrganizationsResolved } = useCurrentOrganization();

	const roles = React.useMemo<readonly RoleView[] | null>(() => accountRoles?.list ?? null, [accountRoles]);

	const flatFacilities = React.useMemo<readonly AccountFacilityView[] | null>(() => {
		if (!areOrganizationsResolved) {
			return null;
		}

		return organizations.flatMap((organization) => organization.facilities);
	}, [areOrganizationsResolved, organizations]);

	const handleInviteClick = React.useCallback(() => {
		openUserSidebar(null);
	}, []);

	const handleResetFiltersClick = React.useCallback(() => {
		setUserFilters(UserFilterView.createDefault());
	}, []);

	const handlePage = React.useCallback((event: PaginatorPageChangeEvent) => {
		setUserFilters(userFiltersSignal.peek().setPageAndRows(event.page, event.rows));
	}, []);

	const inviteButton = canWrite ? (
		<Button
			type="button"
			label="Inviter un utilisateur"
			icon={<UserPlus size={16} />}
			onClick={handleInviteClick}
		/>
	) : undefined;

	const hasActiveFilters = filters.hasActiveFilters();

	// Facility route, no assignment: `facilityId` will never resolve to a value, so `usersList`
	// would otherwise stay `null` forever — checked before the loading skeleton below, and only
	// once facility resolution has actually settled (`isFacilityResolved`), so this never flashes
	// ahead of the genuine loading state.
	if (!canReadAccountUsers && isFacilityResolved && facilityId === null) {
		return (
			<EmptyState
				title="Aucune agence sélectionnée."
				description="Choisissez une agence dans le sélecteur en haut de page pour accéder à ses utilisateurs."
				icon={<Building2 size={28} />}
			/>
		);
	}

	// Skeletons only when there is nothing to show. `isLoading` also turns true when `deps` change —
	// a filter, a page — and taking this branch then would replace an already-readable list with
	// placeholders on every keystroke. With data in hand, the refetch shows as a dimmed, `aria-busy`
	// list instead (`isFetching`, below), which keeps the previous answer legible while the next one
	// travels.
	if (isLoading && usersList === null) {
		return (
			<div css={cardsGridStyle(false)}>
				{skeletonCardIndexes.map((index) => (
					<Skeleton key={index} height="11rem" animation="wave" shape="rectangle" />
				))}
			</div>
		);
	}

	if (usersList === null) {
		return (
			<ErrorState title="La liste des utilisateurs n'a pas pu être chargée." onRetry={refetchUsers} />
		);
	}

	if (usersList.list.length === 0) {
		if (!hasActiveFilters) {
			return (
				<EmptyState
					title="Aucun utilisateur"
					description="Aucun utilisateur pour l'instant."
					icon={<UsersIcon size={28} />}
					action={inviteButton}
				/>
			);
		}

		return (
			<EmptyState
				title="Aucun résultat"
				description="Aucun utilisateur ne correspond à ces filtres."
				icon={<TriangleAlert size={28} />}
				action={
					<Button
						type="button"
						text
						label="Réinitialiser les filtres"
						onClick={handleResetFiltersClick}
					/>
				}
			/>
		);
	}

	return (
		<div css={containerStyle}>
			{error !== null && (
				<Message
					severity="error"
					text="La liste n'a pas pu être actualisée : les données affichées datent du dernier chargement réussi."
				/>
			)}
			<ul css={cardsGridStyle(isFetching)} aria-label="Utilisateurs" aria-busy={isFetching}>
				{usersList.list.map((user) => (
					<UserCard key={user.id} user={user} roles={roles} facilities={flatFacilities} />
				))}
			</ul>
			<Paginator
				first={filters.page * filters.rows}
				rows={filters.rows}
				rowsPerPageOptions={[10, 25, 50, 100]}
				totalRecords={usersList.totalRecords}
				onPageChange={handlePage}
			/>
		</div>
	);
};

export default UsersContent;
